// Componente reutilizable para setear/cambiar contraseña (lote-93).
// Lo usan: modal de rotación forzada, "Mi perfil → cambiar contraseña",
// "Crear usuario nuevo" (en el form de Equipo).
//
// Muestra:
//   - Input de contraseña + input de confirmación
//   - Checklist en vivo: ✓ Mínimo 10 / ✓ Letra / ✓ Dígito / ✓ Símbolo /
//     ✓ No es común / ✓ No es secuencial / ✓ No todos iguales
//   - Strength meter (débil / media / fuerte / excelente)
//   - Botón "Guardar" deshabilitado hasta que cumpla todo + confirme coincida
//
// La validación cliente es solo feedback inmediato — la fuente de verdad es
// la validación server-side (mismo set de reglas en lib/auth.js).
import { useState, useMemo } from './common.jsx';

// Espejo de las reglas de lib/auth.js — mantener sincronizado. Si añades una
// regla allí, añádela aquí para que el feedback en vivo siga coincidiendo.
const RULES = [
  { code: 'too_short',  label: 'Mínimo 10 caracteres', test: (s) => s.length >= 10 },
  { code: 'no_letter',  label: 'Al menos una letra (a-z, A-Z)', test: (s) => /[a-zA-Z]/.test(s) },
  { code: 'no_digit',   label: 'Al menos un dígito (0-9)', test: (s) => /[0-9]/.test(s) },
  { code: 'no_symbol',  label: 'Al menos un símbolo (!@#$%…)', test: (s) => /[^a-zA-Z0-9]/.test(s) },
  { code: 'common',     label: 'No es una contraseña común', test: (s) => !isCommon(s) },
  { code: 'sequential', label: 'No es una secuencia obvia (1234, abcd, qwerty…)', test: (s) => !isSequential(s) },
  { code: 'all_same',   label: 'No tiene todos los caracteres iguales', test: (s) => !(s.length > 0 && s.split('').every(c => c === s[0])) },
];

// Mini lista — el server tiene la lista completa. Si el server rechaza,
// mostramos el error. Esto es solo para feedback inmediato.
const FE_COMMONS = new Set([
  '1234567890', 'password123', 'qwerty1234', 'admin12345', 'welcome123',
  'password1!', 'p@ssw0rd', 'p@ssword1', 'qwertyuiop', 'ucademy123',
  'ucademy2026', 'ucademy2025', 'comisiones', 'changeme12', 'changeme1!',
  'Password1!', 'Password123', 'Welcome1!', 'Admin1234!', 'Test1234!@',
]);
const isCommon = (s) => FE_COMMONS.has(String(s||'').toLowerCase());

const KEYBOARD_ROWS = ['1234567890', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm'];
function isSequential(s) {
  if (!s || s.length < 6) return false;
  const lower = s.toLowerCase();
  for (const row of KEYBOARD_ROWS) {
    const rev = row.split('').reverse().join('');
    for (let len = Math.min(lower.length, row.length); len >= 6; len--) {
      for (let start = 0; start + len <= lower.length; start++) {
        const win = lower.slice(start, start + len);
        if (row.includes(win) || rev.includes(win)) return true;
      }
    }
  }
  let asc = 1, desc = 1;
  for (let i = 1; i < lower.length; i++) {
    const d = lower.charCodeAt(i) - lower.charCodeAt(i - 1);
    asc = d === 1 ? asc + 1 : 1;
    desc = d === -1 ? desc + 1 : 1;
    if (asc >= 6 || desc >= 6) return true;
  }
  return false;
}

// Strength rough — basada en longitud + variedad. Solo para feedback UX, no
// para gating (eso lo hace el ok del validate).
function computeStrength(s) {
  if (!s) return 0;
  let score = 0;
  score += Math.min(s.length * 4, 50);
  if (/[a-z]/.test(s)) score += 8;
  if (/[A-Z]/.test(s)) score += 8;
  if (/[0-9]/.test(s)) score += 8;
  if (/[^a-zA-Z0-9]/.test(s)) score += 12;
  if (s.length >= 14) score += 10;
  if (s.length >= 20) score += 10;
  return Math.min(score, 100);
}
function strengthLabel(score) {
  if (score < 40) return { label: 'Débil',     color: '#df2020' };
  if (score < 60) return { label: 'Media',     color: '#df8a20' };
  if (score < 80) return { label: 'Fuerte',    color: '#0a8e66' };
  return { label: 'Excelente', color: '#0a8e66' };
}

/**
 * Props:
 *   - onSave(newPassword) async  → llamado cuando el user pulsa "Guardar".
 *     Debe lanzar (throw) en error o devolver resuelto en éxito. El
 *     componente desactiva el botón mientras está pendiente.
 *   - showCurrent?: boolean      → si true, añade un campo "Contraseña actual"
 *     que se pasa como `currentPin` a onSave (para "Mi perfil → cambiar
 *     contraseña" donde NO está en rotación forzada).
 *   - submitLabel?: string       → texto del botón. Default: "Guardar".
 *   - cancelLabel?: string       → si se pasa, muestra botón Cancelar.
 *   - onCancel?: () => void      → handler del Cancelar.
 */
export function PasswordSet({ onSave, showCurrent = false, submitLabel = 'Guardar', cancelLabel = null, onCancel = null }) {
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [current, setCurrent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [show, setShow] = useState(false);

  const ruleResults = useMemo(() => RULES.map(r => ({ ...r, pass: r.test(pin) })), [pin]);
  const allPass = ruleResults.every(r => r.pass);
  const matches = pin.length > 0 && pin === pin2;
  const canSubmit = allPass && matches && !saving && (!showCurrent || current.length > 0);
  const strength = computeStrength(pin);
  const strengthMeta = strengthLabel(strength);

  const handleSave = async () => {
    if (!canSubmit) return;
    setSaving(true); setError('');
    try {
      await onSave({ newPin: pin, currentPin: showCurrent ? current : undefined });
      // Limpiar tras éxito para que no quede la contraseña tecleada en el DOM.
      setPin(''); setPin2(''); setCurrent('');
    } catch (err) {
      setError(err?.message || String(err) || 'Error guardando la contraseña');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {showCurrent && (
        <div className="fr">
          <label>Contraseña actual</label>
          <input className="fi" type={show ? 'text' : 'password'} value={current}
            onChange={e => setCurrent(e.target.value)} autoComplete="current-password"/>
        </div>
      )}
      <div className="fr">
        <label>Nueva contraseña</label>
        <input className="fi" type={show ? 'text' : 'password'} value={pin}
          onChange={e => setPin(e.target.value)} autoComplete="new-password"
          placeholder="Mínimo 10 caracteres con letras, dígitos y símbolo"/>
        {/* Strength meter — solo aparece cuando hay algo tecleado */}
        {pin.length > 0 && (
          <div style={{marginTop:6,display:'flex',alignItems:'center',gap:8}}>
            <div style={{flex:1,height:4,background:'var(--bds)',borderRadius:2,overflow:'hidden'}}>
              <div style={{width:strength+'%',height:'100%',background:strengthMeta.color,transition:'width .2s, background .2s'}}/>
            </div>
            <span style={{fontSize:11,fontWeight:600,color:strengthMeta.color,minWidth:64,textAlign:'right'}}>{strengthMeta.label}</span>
          </div>
        )}
      </div>
      <div className="fr">
        <label>Confirma la nueva contraseña</label>
        <input className="fi" type={show ? 'text' : 'password'} value={pin2}
          onChange={e => setPin2(e.target.value)} autoComplete="new-password"/>
        {pin2.length > 0 && !matches && (
          <div style={{marginTop:4,fontSize:11,color:'var(--rd)'}}>Las contraseñas no coinciden.</div>
        )}
      </div>
      <div className="fr" style={{marginBottom:0}}>
        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:11,fontWeight:500,color:'var(--tx2)',cursor:'pointer'}}>
          <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} style={{margin:0}}/>
          Mostrar contraseñas
        </label>
      </div>
      {/* Checklist en vivo */}
      <div style={{marginTop:10,padding:'10px 12px',background:'var(--nc)',borderRadius:8,border:'1px solid var(--bds)'}}>
        <div style={{fontSize:10,fontWeight:700,color:'var(--tx-mu)',textTransform:'uppercase',letterSpacing:'.4px',marginBottom:6}}>
          Requisitos
        </div>
        {ruleResults.map(r => (
          <div key={r.code} style={{
            display:'flex',alignItems:'center',gap:8,fontSize:12,lineHeight:1.7,
            color: r.pass ? 'var(--gr-d)' : 'var(--tx-mu)'
          }}>
            <span style={{
              display:'inline-flex',alignItems:'center',justifyContent:'center',
              width:16,height:16,borderRadius:'50%',flexShrink:0,
              background: r.pass ? 'rgba(10,187,135,.16)' : 'transparent',
              border: r.pass ? '1px solid transparent' : '1px solid var(--bds)',
              color: r.pass ? 'var(--gr-d)' : 'var(--tx3)',
              fontSize:10,fontWeight:700
            }}>{r.pass ? '✓' : '○'}</span>
            {r.label}
          </div>
        ))}
      </div>
      {error && (
        <div style={{
          marginTop:10,padding:'8px 12px',borderRadius:6,fontSize:12,
          background:'rgba(223,32,32,.08)', color:'var(--rd)',
          border:'1px solid rgba(223,32,32,.25)'
        }}>{error}</div>
      )}
      <div style={{marginTop:14,display:'flex',gap:8,justifyContent:'flex-end'}}>
        {cancelLabel && (
          <button type="button" className="btn btn-gh" onClick={onCancel} disabled={saving}>{cancelLabel}</button>
        )}
        <button type="button" className="btn btn-pu" onClick={handleSave} disabled={!canSubmit}>
          {saving ? 'Guardando…' : submitLabel}
        </button>
      </div>
    </div>
  );
}
